import React, { memo, useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import pxToRem from "~libs/pxToRem";
import Selector, { type OptionKV } from "~styled/selector/Selector";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import {
  motivoRechazoSiniestroOptions,
  motivoRechazoSiniestroFilter,
} from "~constants/motivoRechazoSiniestro";

const useStyles = makeStyles((theme: any) => ({
  root: { display: "flex", height: "100%" },
  compania: {
    textAlign: "center",
    fontWeight: 900,
    color: theme.colours.violeta,
    paddingTop: 15,
    paddingBottom: 8,
    display: "block",
    flex: 1,
  },
  tipoPoliza: {
    textAlign: "center",
    fontWeight: 900,
    color: "#afafaf",
    textTransform: "uppercase",
  },
  polizaTitle: { fontSize: pxToRem(12) },
  content: { paddingBottom: 50 },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    padding: "5%",
    alignItems: "center",
    maxWidth: 650,
    width: "100%",
  },
  button: {
    width: "40vw",
    maxWidth: 200,
    background: theme.colours.verdeMgBroker,
    borderRadius: 50,
    border: 0,
    color: theme.colours.white,
    boxShadow: "2px 2px 10px 0 #CCCCE4",
    height: 47,
    fontWeight: 900,
    fontSize: 16,
    padding: "0 20px",
    margin: "0 10px",
    textTransform: "none",
    "&:disabled": { background: "#616161", color: "#C4C4C4" },
  },
  buttonBack: {
    width: "40vw",
    maxWidth: 200,
    background: theme.colours.white,
    color: "#455597",
    borderRadius: 50,
    border: 0,
    boxShadow: "2px 2px 10px 0 #CCCCE4",
    height: 47,
    fontWeight: 900,
    fontSize: 16,
    padding: "0 20px",
    margin: "0 10px",
    textTransform: "none",
    "&:disabled": { background: "#616161", color: "#C4C4C4" },
  },
  sentenceTitle: {
    textAlign: "center",
    fontWeight: 900,
    color: "#afafaf",
    textTransform: "uppercase",
    paddingTop: "2vh",
    paddingBottom: "2vh",
  },
  motivoSelector: { position: "absolute", width: "90%" },
}));

type V = string | number;
type EstadoDestino = "PendienteCompania" | "Rechazado" | "Resuelto" | string;

export interface PopupEstadoProps {
  isOpened: boolean;
  onClose: () => void;
  siniestroId: string | number;
  compania: string;
  nroPoliza: string | number;
  nombre: string;
  nuevoEstado: EstadoDestino;
  onClickAceptar: (
    siniestroId: string | number,
    nuevoEstado: EstadoDestino,
    motivoRechazo: V | null,
    comentario: string,
    numeroSiniestroCompania: string
  ) => void;
}

function PopupEstadoBase({
  isOpened,
  onClose,
  siniestroId,
  compania,
  nroPoliza,
  nombre,
  onClickAceptar,
  nuevoEstado,
}: PopupEstadoProps): JSX.Element {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const tituloPopup =
    nuevoEstado === "PendienteCompania"
      ? "Pasar a Pendiente en Compañía"
      : nuevoEstado === "Rechazado"
      ? "Rechazar"
      : "Resolver";

  const [motivoRechazo, setMotivoRechazo] = useState<V | null>(null);
  const [comentario, setComentario] = useState<string>("");
  const [numeroSiniestroCompania, setNumeroSiniestroCompania] =
    useState<string>("");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (nuevoEstado === "PendienteCompania") {
      setEnabled(!!numeroSiniestroCompania);
    } else if (nuevoEstado === "Rechazado") {
      setEnabled(motivoRechazo !== null && motivoRechazo !== "");
    } else {
      setEnabled(true);
    }
  }, [numeroSiniestroCompania, motivoRechazo, nuevoEstado]);

  const handleChangeMotivoRechazo = (motivo: V) => setMotivoRechazo(motivo);
  const handleChangeComentario = (value: string) => setComentario(value);
  const handleChangeNumeroSiniestroCompania = (value: string) =>
    setNumeroSiniestroCompania(value);

  const motivoLabelMap = motivoRechazoSiniestroOptions as Record<
    string,
    string
  >;

  return (
    <div className={classes.root}>
      <Dialog
        open={isOpened}
        scroll="body"
        onClose={onClose}
        fullScreen={fullScreen}
      >
        <DialogTitle style={{ padding: 0 }}>
          <div style={{ display: "flex", height: "100%" }}>
            <div style={{ width: 48 }} />
            <Typography variant="h5" className={classes.compania}>
              {tituloPopup}
            </Typography>
            <div style={{ width: 48 }}>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <Typography variant="subtitle2" className={classes.tipoPoliza}>
            <span className={classes.polizaTitle}>Compañía:</span> {compania}
          </Typography>
          <Typography variant="subtitle2" className={classes.tipoPoliza}>
            <span className={classes.polizaTitle}>Número de Póliza:</span>{" "}
            {nroPoliza}
          </Typography>
          <Typography variant="subtitle2" className={classes.tipoPoliza}>
            <span className={classes.polizaTitle}>Asegurado:</span> {nombre}
          </Typography>

          {nuevoEstado === "PendienteCompania" && (
            <DialogContent className={classes.content}>
              <Typography variant="subtitle2" className={classes.sentenceTitle}>
                NÚMERO DE SINIESTRO DE COMPAÑÍA:
              </Typography>
              <div className={classes.motivoSelector}>
                <TextField
                  id="numeroSiniestroCompania"
                  size="small"
                  style={{ width: "100%" }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeNumeroSiniestroCompania(e.target.value)
                  }
                  value={numeroSiniestroCompania}
                  variant="outlined"
                />
              </div>
            </DialogContent>
          )}

          {nuevoEstado === "Rechazado" && (
            <>
              <DialogContent className={classes.content}>
                <Divider />
                <Typography
                  variant="subtitle2"
                  className={classes.sentenceTitle}
                >
                  MOTIVO
                </Typography>
                <div className={classes.motivoSelector}>
                  <Selector<V>
                    id="motivoRechazo"
                    value={
                      motivoRechazo != null
                        ? motivoLabelMap[String(motivoRechazo)]
                        : ""
                    }
                    options={motivoRechazoSiniestroFilter as OptionKV<V>[]}
                    onChange={handleChangeMotivoRechazo}
                    variant="big"
                  />
                </div>
              </DialogContent>

              <DialogContent className={classes.content}>
                <Typography
                  variant="subtitle2"
                  className={classes.sentenceTitle}
                >
                  COMENTARIO
                </Typography>
                <div className={classes.motivoSelector}>
                  <TextField
                    id="comentario"
                    size="small"
                    style={{ width: "100%" }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeComentario(e.target.value)
                    }
                    value={comentario}
                    variant="outlined"
                  />
                </div>
              </DialogContent>
            </>
          )}
        </DialogTitle>

        <div className={classes.buttonContainer}>
          <Button
            className={classes.buttonBack}
            onClick={onClose}
            variant="contained"
          >
            CANCELAR
          </Button>
          <Button
            className={classes.button}
            onClick={() =>
              onClickAceptar(
                siniestroId,
                nuevoEstado,
                motivoRechazo,
                comentario,
                numeroSiniestroCompania
              )
            }
            variant="contained"
            color="primary"
            disabled={!enabled}
          >
            CONFIRMAR
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

export default memo<PopupEstadoProps>(PopupEstadoBase);
